import React, { useState, useEffect, useMemo } from 'react';
import {
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline';
import { DefaultService, GameOutput, LeagueOutput, LeaguePlayer, Player, PlayerWithLeagueContext } from '../../../generated-api';
import { Team, TEAM_COLORS } from '../../SortableEntity';
import LeagueChipToggle from '../LeagueChipToggle';
import PlayerAvatar from '../../common/PlayerAvatar';
import CompactRatingDisplay from '../../common/CompactRatingDisplay';

interface ConfirmStartStepProps {
  selectedGame: GameOutput | null;
  gameExtensions: string[];
  selectedPlayerIds: string[];
  players: (Player | PlayerWithLeagueContext)[];
  teams: Team[];
  leagues: LeagueOutput[];
  selectedLeagueIds: string[];
  onToggleLeague: (leagueId: string) => void;
  gameSpecificLeagues: LeagueOutput[];
  onSetLeagueIds: (leagueIds: string[]) => void;
}

const ConfirmStartStep: React.FC<ConfirmStartStepProps> = ({
  selectedGame,
  gameExtensions,
  selectedPlayerIds,
  players,
  teams,
  leagues,
  selectedLeagueIds,
  onToggleLeague,
  gameSpecificLeagues,
  onSetLeagueIds,
}) => {
  const [displayLeagueId, setDisplayLeagueId] = useState<string>(selectedLeagueIds[0] ?? '');
  const [leaguePlayerRatings, setLeaguePlayerRatings] = useState<Map<string, LeaguePlayer>>(new Map());

  // Combine context leagues with game-specific leagues for lookups
  const allLeagues = useMemo(() => {
    const ids = new Set(leagues.map(l => l.id));
    return [...leagues, ...gameSpecificLeagues.filter(l => !ids.has(l.id))];
  }, [leagues, gameSpecificLeagues]);

  // Reset displayLeagueId if it's no longer in the selection
  useEffect(() => {
    if (!selectedLeagueIds.includes(displayLeagueId)) {
      setDisplayLeagueId(selectedLeagueIds[0] ?? '');
    }
  }, [selectedLeagueIds, displayLeagueId]);

  // Fetch player ratings from the displayed league
  useEffect(() => {
    if (!displayLeagueId || selectedPlayerIds.length === 0) return;

    DefaultService.getLeaguePlayersLeaguesLeagueIdPlayersGet({ leagueId: displayLeagueId })
      .then((leaguePlayers) => {
        const ratingsMap = new Map<string, LeaguePlayer>();
        for (const lp of leaguePlayers) {
          ratingsMap.set(lp.playerId, lp);
        }
        setLeaguePlayerRatings(ratingsMap);
      })
      .catch((err) => {
        console.error('Failed to load league player ratings:', err);
      });
  }, [displayLeagueId, selectedPlayerIds]);

  const unrankedSelected = selectedLeagueIds.some(id => {
    const l = allLeagues.find(le => le.id === id);
    return l?.name === 'Unranked';
  });

  const isRanked = !unrankedSelected;

  const customLeagues = allLeagues.filter(l => {
    if (l.isGlobal || l.isGameSpecificLeague || l.name === 'Unranked') return false;
    const membersInPlay = l.playerIds.filter(id => selectedPlayerIds.includes(id));
    return membersInPlay.length >= 2;
  });

  return (
    <div className="flex flex-col gap-4">
      {/* Game summary card */}
      {selectedGame && (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-edge bg-surface">
          <div className="w-20 h-20 rounded-lg overflow-hidden bg-surface-subtle shrink-0">
            {selectedGame.imageUrl ? (
              <img
                src={selectedGame.imageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center
                              bg-gradient-to-br from-primary-50 to-indigo-50
                              dark:from-primary-900/20 dark:to-indigo-900/20">
                <PuzzlePieceIcon className="w-8 h-8 text-primary-300" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-serif font-semibold text-heading truncate">
              {selectedGame.name}
            </h3>
            {gameExtensions.length > 0 && (
              <div className="flex gap-1 mt-1 flex-wrap">
                {gameExtensions.map(ext => (
                  <span
                    key={ext}
                    className="px-1.5 py-0.5 rounded-full text-[10px] font-medium
                               bg-accent-500/10 text-accent-600 dark:text-accent-400"
                  >
                    {ext}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ranked / Unranked choice */}
      <div>
        <h3 className="text-xs font-medium text-muted uppercase tracking-wide mb-2">League Mode</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* Ranked card */}
          <button
            data-track="wizard_ranked"
            type="button"
            onClick={() => {
              if (isRanked) return;
              const globalLeague = allLeagues.find(l => l.isGlobal);
              const gameLeague = gameSpecificLeagues[0];
              const ids = [globalLeague?.id, gameLeague?.id].filter(Boolean) as string[];
              onSetLeagueIds(ids);
            }}
            className={`flex flex-col items-center gap-1 p-4 rounded-xl border transition-all duration-150
                        active:scale-[0.97] motion-reduce:active:scale-100
                        ${isRanked
                          ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-[var(--bg-base)] border-primary-300 bg-surface shadow-sm'
                          : 'border-edge bg-surface hover:border-edge-input hover:shadow-sm'
                        }`}
            aria-pressed={isRanked}
          >
            <span className="text-2xl">🌍</span>
            <span className="text-sm font-semibold text-heading">Ranked</span>
            <span className="text-[11px] text-muted">Ratings tracked</span>
          </button>

          {/* Unranked card */}
          <button
            data-track="wizard_unranked"
            type="button"
            onClick={() => {
              if (unrankedSelected) return;
              const unrankedLeague = allLeagues.find(l => l.name === 'Unranked');
              if (unrankedLeague) onSetLeagueIds([unrankedLeague.id]);
            }}
            className={`flex flex-col items-center gap-1 p-4 rounded-xl border transition-all duration-150
                        active:scale-[0.97] motion-reduce:active:scale-100
                        ${unrankedSelected
                          ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-[var(--bg-base)] border-primary-300 bg-surface shadow-sm'
                          : 'border-edge bg-surface hover:border-edge-input hover:shadow-sm'
                        }`}
            aria-pressed={unrankedSelected}
          >
            <span className="text-2xl">🎲</span>
            <span className="text-sm font-semibold text-heading">Unranked</span>
            <span className="text-[11px] text-muted">Just for fun</span>
          </button>
        </div>

        {/* Custom league chips — only when ranked and custom leagues exist */}
        {isRanked && customLeagues.length > 0 && (
          <div className="mt-3">
            <span className="text-[11px] text-muted block mb-1.5">Also track in:</span>
            <div className="flex flex-wrap gap-2">
              {customLeagues.map(league => (
                <LeagueChipToggle
                  key={league.id}
                  league={league}
                  isSelected={selectedLeagueIds.includes(league.id)}
                  isDisabled={false}
                  onToggle={onToggleLeague}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Players summary */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium text-muted uppercase tracking-wide">
            Players ({selectedPlayerIds.length})
          </h3>
          {!unrankedSelected && selectedLeagueIds.length > 1 && (
            <div className="flex gap-1">
              {selectedLeagueIds.map(leagueId => {
                const league = allLeagues.find(l => l.id === leagueId);
                if (!league) return null;
                const isActive = leagueId === displayLeagueId;
                return (
                  <button
                    data-track="wizard_league_tab"
                    key={leagueId}
                    type="button"
                    onClick={() => setDisplayLeagueId(leagueId)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors
                               ${isActive
                                 ? 'bg-primary-500 text-white'
                                 : 'bg-surface-subtle text-muted hover:bg-surface-active'
                               }`}
                  >
                    {league.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="space-y-2">
          {/* Team groups */}
          {teams.map(team => {
            const teamColor = TEAM_COLORS.find(c => c.name.toLowerCase() === team.color) || null;

            const memberRatings = team.playerIds
              .map(pid => leaguePlayerRatings.get(pid))
              .filter((lp): lp is LeaguePlayer => !!lp);
            const teamRating = memberRatings.length > 0
              ? memberRatings.reduce((sum, lp) => sum + lp.rating, 0)
              : null;
            const teamSigma = memberRatings.length > 0
              ? Math.sqrt(memberRatings.reduce((sum, lp) => sum + lp.sigma * lp.sigma, 0))
              : null;

            return (
              <div
                key={team.id}
                className={`rounded-xl border overflow-hidden
                           ${teamColor ? `${teamColor.border}` : 'border-edge'}`}
              >
                {/* Team header */}
                <div className={`flex items-center justify-between px-3 py-2
                                ${teamColor ? `${teamColor.bg}` : 'bg-surface-subtle'}`}>
                  <span className={`text-xs font-bold uppercase tracking-wider
                                   ${teamColor ? teamColor.text : 'text-muted'}`}>
                    {team.name}
                  </span>
                  {!unrankedSelected && teamRating !== null && teamSigma !== null && (
                    <CompactRatingDisplay rating={teamRating} sigma={teamSigma} className="text-[10px]" />
                  )}
                </div>
                {/* Team members */}
                <div className="divide-y divide-edge">
                  {team.playerIds.map(playerId => {
                    const player = players.find(p => p.id === playerId);
                    if (!player) return null;

                    const leaguePlayer = leaguePlayerRatings.get(playerId);
                    const inLeague = !!leaguePlayer;

                    return (
                      <div
                        key={playerId}
                        className={`flex items-center gap-3 px-3 py-2.5
                                   ${!unrankedSelected && !inLeague ? 'opacity-60 bg-surface-subtle' : 'bg-surface'}`}
                      >
                        <div className="shrink-0">
                          <PlayerAvatar name={player.name} size="medium" imageUrl={player.imageUrl} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-heading truncate block">{player.name}</span>
                          {!unrankedSelected && (
                            <div className="mt-0.5">
                              {inLeague ? (
                                <CompactRatingDisplay rating={leaguePlayer.rating} sigma={leaguePlayer.sigma} className="text-[10px]" />
                              ) : (
                                <span className="text-[10px] text-disabled italic">Not in league</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Solo players (not in any team) */}
          {selectedPlayerIds
            .filter(pid => !teams.some(t => t.playerIds.includes(pid)))
            .map(playerId => {
              const player = players.find(p => p.id === playerId);
              if (!player) return null;

              const leaguePlayer = leaguePlayerRatings.get(playerId);
              const inLeague = !!leaguePlayer;

              return (
                <div
                  key={playerId}
                  className={`flex items-center gap-3 p-3 rounded-xl border
                             ${!unrankedSelected && !inLeague
                               ? 'bg-surface-subtle border-edge opacity-60'
                               : 'bg-surface border-edge'
                             }`}
                >
                  <div className="shrink-0">
                    <PlayerAvatar name={player.name} size="medium" imageUrl={player.imageUrl} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-heading truncate block">{player.name}</span>
                    {!unrankedSelected && (
                      <div className="mt-1">
                        {inLeague ? (
                          <CompactRatingDisplay rating={leaguePlayer.rating} sigma={leaguePlayer.sigma} className="text-[10px]" />
                        ) : (
                          <span className="text-[10px] text-disabled italic">Not in league</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default ConfirmStartStep;
